/**
 * Reaching the microphone, which this page does not have.
 *
 * A browser cannot record a fifty-minute talk with the screen locked — it suspends the
 * page and the recording dies. The installed applications can, so the button lives here
 * and the microphone lives there.
 *
 * Two shells, two ways of being called: Android injects an object with methods, iOS takes
 * a posted message. Both answer the same way, by calling a function this file puts on the
 * window, because a permission prompt cannot be waited on from inside the call.
 *
 * On the website neither shell exists, `shell()` answers null, and no record button is
 * ever drawn. A teacher is never offered something that would not work.
 */

export interface ShellCapabilities {
  canRecord: boolean;
  microphoneGranted: boolean;
  platform: "ios" | "android";
}

/**
 * Which door the finished recording reports through.
 *
 * A teacher in a seat and a phone left at the front of a room are different things: they
 * sign in differently, and the archive has to be able to tell them apart when it chooses
 * which copy to keep. The shell needs telling, because it makes the call after she has
 * closed the page.
 */
export type RecordingSource = "attendee" | "room";

export interface StartResult {
  started: boolean;
  scheduleItemId?: string;
  problem?: string | null;
}

export interface StopResult {
  stopped: boolean;
  recordedSeconds: number;
  problem?: string | null;
}

const BRIDGE = "VerityConnectRecording";

interface AndroidBridge {
  capabilities(): string;
  currentRecording(): string;
  start(scheduleItemId: string, title: string, source: string, callbackId: string): void;
  stop(callbackId: string): void;
}

interface AppleBridge {
  postMessage(message: Record<string, unknown>): void;
}

type Pending = (payload: string) => void;

const pending = new Map<string, Pending>();
let nextId = 0;

function windowWithBridges(): (Window & {
  [BRIDGE]?: AndroidBridge;
  webkit?: { messageHandlers?: Record<string, AppleBridge> };
  __verityRecordingAnswer?: (id: string, payload: string) => void;
}) | null {
  return typeof window === "undefined" ? null : window;
}

/**
 * Installed once, and only when a shell is present.
 *
 * Both shells call this by name to hand back an answer. Registering it on the website too
 * would leave a function nothing ever calls.
 */
function ensureAnswering(): void {
  const host = windowWithBridges();
  if (!host || host.__verityRecordingAnswer) return;
  host.__verityRecordingAnswer = (id, payload) => {
    const waiting = pending.get(id);
    if (!waiting) return;
    pending.delete(id);
    waiting(payload);
  };
}

/** Which shell this is running inside, or null on the website. */
export function shell(): "ios" | "android" | null {
  const host = windowWithBridges();
  if (!host) return null;
  if (typeof host[BRIDGE]?.start === "function") return "android";
  if (host.webkit?.messageHandlers?.[BRIDGE]) return "ios";
  return null;
}

function ask<T>(action: string, extra: Record<string, unknown> = {}): Promise<T | null> {
  const host = windowWithBridges();
  const platform = shell();
  if (!host || !platform) return Promise.resolve(null);
  ensureAnswering();

  const callbackId = `rec-${++nextId}`;
  return new Promise<T | null>((resolve) => {
    // A shell that never answers must not leave a button spinning for the rest of the
    // session. Two minutes is long enough for a permission prompt somebody walked away
    // from, and short enough that she can try again.
    const timer = window.setTimeout(() => {
      pending.delete(callbackId);
      resolve(null);
    }, 120_000);

    pending.set(callbackId, (payload) => {
      window.clearTimeout(timer);
      try {
        resolve(JSON.parse(payload) as T);
      } catch {
        resolve(null);
      }
    });

    if (platform === "android") {
      const bridge = host[BRIDGE]!;
      if (action === "start") {
        bridge.start(
          String(extra.scheduleItemId ?? ""),
          String(extra.title ?? ""),
          String(extra.source ?? "attendee"),
          callbackId,
        );
      } else if (action === "stop") {
        bridge.stop(callbackId);
      } else {
        // Android answers these two straight away rather than by callback, because
        // neither can prompt and a round trip would only delay drawing the button.
        window.clearTimeout(timer);
        pending.delete(callbackId);
        const answer = action === "capabilities" ? bridge.capabilities() : bridge.currentRecording();
        try {
          resolve(JSON.parse(answer) as T);
        } catch {
          resolve(null);
        }
      }
      return;
    }

    host.webkit!.messageHandlers![BRIDGE]!.postMessage({ action, callbackId, ...extra });
  });
}

export async function capabilities(): Promise<ShellCapabilities | null> {
  return ask<ShellCapabilities>("capabilities");
}

/** Which session is recording right now, or null. Survives the page reloading. */
export async function currentRecording(): Promise<string | null> {
  const answer = await ask<{ scheduleItemId: string | null }>("currentRecording");
  return answer?.scheduleItemId ?? null;
}

export async function startRecording(
  scheduleItemId: string,
  title: string,
  source: RecordingSource = "attendee",
): Promise<StartResult> {
  const answer = await ask<StartResult>("start", { scheduleItemId, title, source });
  return answer ?? { started: false, problem: "Recording could not be started on this phone." };
}

export async function stopRecording(): Promise<StopResult> {
  const answer = await ask<StopResult>("stop");
  return answer ?? { stopped: false, recordedSeconds: 0, problem: "The recording could not be stopped." };
}
